import type { AuditEntry, Initiative, InitiativeLink, LinkType, Product, Team } from "./types";

const ADMIN_KEY = "planning-admin-pwd";
const ACTOR_KEY = "planning-admin-actor";

export function getAdminPassword(): string | null {
  return sessionStorage.getItem(ADMIN_KEY);
}

export function setAdminPassword(pwd: string) {
  sessionStorage.setItem(ADMIN_KEY, pwd);
}

export function clearAdminPassword() {
  sessionStorage.removeItem(ADMIN_KEY);
}

export function getActor(): string {
  return sessionStorage.getItem(ACTOR_KEY) ?? "";
}

export function setActor(name: string) {
  sessionStorage.setItem(ACTOR_KEY, name);
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  const pwd = getAdminPassword();
  if (pwd) headers.set("x-admin-password", pwd);
  const actor = getActor();
  if (actor) headers.set("x-actor", actor);
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as any).error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  teams:        () => req<Team[]>("/api/teams"),
  products:     () => req<Product[]>("/api/products"),
  initiatives:  (quarter?: string) =>
    req<Initiative[]>(`/api/initiatives${quarter ? `?quarter=${quarter}` : ""}`),
  initiative:   (id: number) => req<Initiative>(`/api/initiatives/${id}`),
  create:       (body: Partial<Initiative>) =>
    req<Initiative>("/api/initiatives", { method: "POST", body: JSON.stringify(body) }),
  update:       (id: number, body: Partial<Initiative>) =>
    req<Initiative>(`/api/initiatives/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  remove:       (id: number) =>
    req<{ ok: true }>(`/api/initiatives/${id}`, { method: "DELETE" }),
  audit:        (id: number) => req<AuditEntry[]>(`/api/audit/${id}`),
  links:        () => req<InitiativeLink[]>("/api/links"),
  createLink:   (from_id: number, to_id: number, type: LinkType) =>
    req<InitiativeLink>("/api/links", {
      method: "POST",
      body: JSON.stringify({ from_id, to_id, type }),
    }),
  deleteLink:   (id: number) =>
    req<{ ok: true }>(`/api/links/${id}`, { method: "DELETE" }),
  login:        (password: string) =>
    req<{ ok: true }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
};
